import { useMemo } from 'react';
import SubscriptionNotice from '@/components/SubscriptionNotice';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { useOwners } from '@/hooks/useOwners';
import { usePatients } from '@/hooks/usePatients';
import { useAlertCounts, useAlerts } from '@/hooks/useAlerts';
import { usePendingServiceOrders } from '@/hooks/useServiceOrders';
import { useTodayGroomingAppointments } from '@/hooks/useGrooming';
import { useGroomingCancellationsByOwner } from '@/hooks/useStats';
import { useCashRegisterStatus } from '@/hooks/useCashRegister';
import { useAuthStore } from '@/context/authStore';
import { ModulePermission } from '@/types';
import { STATUS_LABELS } from '@/services/grooming';
import {
  Package,
  Users,
  PawPrint,
  ShoppingCart,
  AlertTriangle,
  TrendingDown,
  Calendar,
  ArrowRight,
  Activity,
  ClipboardList,
  DollarSign,
  Scissors,
  Clock,
  Banknote,
  CreditCard,
  Wallet,
  TrendingUp,
  CheckCircle2,
  XCircle,
  BarChart3,
  Ban,
} from 'lucide-react';
import clsx from 'clsx';

// Huella decorativa para el estilo (con animación opcional)
const PawPrintDecor = ({ className, style, animated }: { className?: string; style?: React.CSSProperties; animated?: boolean }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    {animated ? (
      <>
        {/* Almohadilla central */}
        <ellipse cx="16" cy="22" rx="7" ry="6">
          <animate attributeName="ry" values="6;5.5;6" dur="2s" repeatCount="indefinite" />
        </ellipse>
        {/* Dedos con animación de "caminar" */}
        <ellipse cx="7" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0s" />
        </ellipse>
        <ellipse cx="13" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.2s" />
        </ellipse>
        <ellipse cx="19" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.4s" />
        </ellipse>
        <ellipse cx="25" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0.6s" />
        </ellipse>
      </>
    ) : (
      <>
        <ellipse cx="16" cy="22" rx="7" ry="6"/>
        <ellipse cx="7" cy="12" rx="4" ry="4.5"/>
        <ellipse cx="13" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="19" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="25" cy="12" rx="4" ry="4.5"/>
      </>
    )}
  </svg>
);

export default function Dashboard() {
  const { user, tenant } = useAuthStore();

  // Obtener permisos efectivos
  const permissions = useMemo(() => {
    return user?.effective_permissions || [];
  }, [user?.effective_permissions]);

  const hasPermission = (permission: ModulePermission): boolean => {
    return permissions.includes(permission);
  };

  // Determinar si es rol de tesorero/cajero
  const isCashierRole = useMemo(() => {
    return user?.role === 'cashier' ||
           (hasPermission('cash_register') && hasPermission('sales') && !hasPermission('patients'));
  }, [user?.role, permissions]);

  // Solo cargar datos si el usuario tiene permiso
  const { data: products } = useProducts({}, { enabled: hasPermission('products') });
  const { data: owners } = useOwners({}, { enabled: hasPermission('owners') });
  const { data: patients } = usePatients({}, { enabled: hasPermission('patients') });
  const { data: lowStockProducts } = useProducts({ only_low_stock: true }, { enabled: hasPermission('products_write') });
  const { data: alertCounts } = useAlertCounts({ enabled: hasPermission('alerts') });
  const { data: recentAlerts } = useAlerts({ limit: 5, enabled: hasPermission('alerts') });
  const { data: pendingOrders } = usePendingServiceOrders({ enabled: hasPermission('service_orders') || hasPermission('sales') });
  const { data: todayGrooming } = useTodayGroomingAppointments({ enabled: hasPermission('grooming') });
  const { data: cashStatus } = useCashRegisterStatus({ enabled: hasPermission('cash_register') });
  const { data: groomingCancellations } = useGroomingCancellationsByOwner({ enabled: hasPermission('grooming') });

  // Filtrar turnos de hoy que no están cancelados
  const activeGroomingToday = todayGrooming?.filter(apt => apt.status !== 'cancelled') || [];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const alertTypeIcons: Record<string, typeof TrendingDown> = {
    low_stock: TrendingDown,
    expiration: Calendar,
    next_visit: PawPrint,
  };

  const alertTypeColors: Record<string, string> = {
    low_stock: 'text-amber-600 bg-amber-100',
    expiration: 'text-orange-600 bg-orange-100',
    next_visit: 'text-blue-600 bg-blue-100',
  };

  const alertTypeLabels: Record<string, string> = {
    low_stock: 'Stock bajo',
    expiration: 'Por vencer',
    next_visit: 'Próxima visita',
  };

  // Formatear moneda
  const formatCurrency = (value: number | string | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value || 0;
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num);
  };

  // Si es tesorero/cajero, mostrar dashboard especializado
  if (isCashierRole) {
    return <CashierDashboard
      user={user}
      tenant={tenant}
      cashStatus={cashStatus}
      pendingOrders={pendingOrders || []}
      formatCurrency={formatCurrency}
      getGreeting={getGreeting}
      hasPermission={hasPermission}
    />;
  }

  // Dashboard Admin/General
  return (
    <div className="space-y-6">
      {/* Aviso de suscripción: solo aparece si hay algo que hacer */}
      <SubscriptionNotice />

      {/* Header con estilo login */}
      <div className="relative overflow-hidden bg-gradient-to-br from-taupe-600 via-taupe-700 to-taupe-800 rounded-2xl p-8">
        {/* Decoraciones */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cream-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cream-300/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
          <PawPrintDecor animated className="absolute w-24 h-24 text-cream-300/10 top-4 right-8 rotate-12" />
          <PawPrintDecor animated className="absolute w-16 h-16 text-cream-300/10 bottom-4 right-32 -rotate-12" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-cream-300 text-sm font-medium">{tenant?.name || 'ANIMAvet'}</span>
            </div>
            <h1 className="text-3xl font-bold text-cream-50">
              {getGreeting()}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-taupe-200 mt-1">
              Panel de administración
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission('sales') && (
              <Link
                to="/dashboard/sales?tab=new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cream-100 text-taupe-800 rounded-xl font-medium hover:bg-white transition-all shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="h-4 w-4" />
                Nueva Venta
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Banner Caja Cerrada - visible para quienes tienen permiso */}
      {hasPermission('cash_register') && cashStatus && !cashStatus.is_open && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Banknote className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">La caja está cerrada</h3>
                <p className="text-amber-100">Haz clic aquí para abrir la caja del día</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-5 py-3 rounded-xl group-hover:bg-white/30 transition-all">
              <span className="text-white font-semibold">Abrir Caja</span>
              <ArrowRight className="h-5 w-5 text-white group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Caja abierta - resumen rápido */}
      {hasPermission('cash_register') && cashStatus?.is_open && cashStatus.current_register && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-green-100 text-sm">Caja abierta</p>
                <p className="text-white font-bold">
                  Ventas: {formatCurrency(cashStatus.current_register.total_sales)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-white/90 text-sm">
              <div className="text-center">
                <p className="text-green-200 text-xs">Efectivo</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_cash_sales)}</p>
              </div>
              <div className="text-center">
                <p className="text-green-200 text-xs">Tarjeta</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_card_sales)}</p>
              </div>
              <div className="text-center">
                <p className="text-green-200 text-xs">Transferencia</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_transfer_sales)}</p>
              </div>
              <ArrowRight className="h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {hasPermission('owners') && (
          <Link to="/dashboard/owners" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <Users className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{owners?.length || 0}</p>
            <p className="text-sm text-taupe-500">Clientes</p>
          </Link>
        )}

        {hasPermission('patients') && (
          <Link to="/dashboard/patients" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <PawPrint className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{patients?.length || 0}</p>
            <p className="text-sm text-taupe-500">Pacientes</p>
          </Link>
        )}

        {hasPermission('products') && (
          <Link to="/dashboard/products" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
                <Package className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{products?.length || 0}</p>
            <p className="text-sm text-taupe-500">Productos</p>
          </Link>
        )}

        {hasPermission('alerts') && (
          <Link to="/dashboard/alerts" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className={clsx(
                'w-11 h-11 rounded-xl flex items-center justify-center shadow-md',
                (alertCounts?.total || 0) > 0
                  ? 'bg-gradient-to-br from-red-500 to-red-600'
                  : 'bg-gradient-to-br from-taupe-400 to-taupe-500'
              )}>
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{alertCounts?.total || 0}</p>
            <p className="text-sm text-taupe-500">Alertas</p>
          </Link>
        )}
      </div>

      {/* Contenido principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alertas - lado izquierdo */}
        {hasPermission('alerts') && (
          <div className="lg:col-span-2 bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-cream-100 flex items-center justify-between bg-cream-50/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
                <h2 className="font-semibold text-taupe-800">Alertas Recientes</h2>
              </div>
              <Link to="/dashboard/alerts" className="text-sm text-taupe-600 hover:text-taupe-800 font-medium">
                Ver todas →
              </Link>
            </div>

            <div className="p-4">
              {recentAlerts && recentAlerts.length > 0 ? (
                <div className="space-y-3">
                  {recentAlerts.map((alert) => {
                    const Icon = alertTypeIcons[alert.alert_type];
                    const colorClass = alertTypeColors[alert.alert_type];
                    return (
                      <div
                        key={alert.id}
                        className={clsx(
                          'flex items-start gap-4 p-4 rounded-xl transition-colors',
                          alert.is_read ? 'bg-cream-50' : 'bg-white border border-cream-200 shadow-sm'
                        )}
                      >
                        <div className={clsx('p-2.5 rounded-xl', colorClass)}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-taupe-800">{alert.title}</p>
                            <span className={clsx('text-xs px-2 py-0.5 rounded-full flex-shrink-0', colorClass)}>
                              {alertTypeLabels[alert.alert_type]}
                            </span>
                          </div>
                          {alert.message && (
                            <p className="text-sm text-taupe-500 mt-1 line-clamp-1">{alert.message}</p>
                          )}
                        </div>
                        {!alert.is_read && (
                          <span className="w-2.5 h-2.5 bg-taupe-500 rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Activity className="h-8 w-8 text-taupe-400" />
                  </div>
                  <p className="text-taupe-600 font-medium">Sin alertas pendientes</p>
                  <p className="text-sm text-taupe-400 mt-1">Todo está bajo control</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Panel lateral derecho */}
        <div className={clsx('space-y-5', !hasPermission('alerts') && 'lg:col-span-2')}>
          {/* Comandas pendientes */}
          {(hasPermission('service_orders') || hasPermission('sales')) && (pendingOrders?.length || 0) > 0 && (
            <Link
              to="/dashboard/sales?tab=orders"
              className="block bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200 hover:border-orange-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
                    <ClipboardList className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-taupe-800">Comandas Pendientes</h3>
                    <p className="text-sm text-orange-600">{pendingOrders?.length} por cobrar</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-orange-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          )}

          {/* Peluquería hoy */}
          {hasPermission('grooming') && activeGroomingToday.length > 0 && (
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-5 border border-pink-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
                    <Scissors className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-taupe-800">Peluquería Hoy</h3>
                    <p className="text-sm text-pink-600">{activeGroomingToday.length} turnos</p>
                  </div>
                </div>
                <Link to="/dashboard/grooming" className="text-sm font-medium text-pink-600 hover:text-pink-800">
                  Ver →
                </Link>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {activeGroomingToday.slice(0, 4).map((apt) => (
                  <div
                    key={apt.id}
                    className={clsx(
                      'flex items-center justify-between p-3 rounded-xl',
                      apt.status === 'completed' ? 'bg-green-100 border border-green-200' :
                      'bg-white border border-pink-100'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-taupe-400" />
                      <span className="font-medium text-taupe-800">{apt.scheduled_time?.slice(0, 5)}</span>
                      <span className="text-taupe-400">-</span>
                      <span className="text-taupe-600">{apt.patient_name}</span>
                    </div>
                    <span className={clsx(
                      'text-xs px-2 py-1 rounded-full font-medium',
                      apt.status === 'pending' && 'bg-yellow-100 text-yellow-700',
                      apt.status === 'completed' && 'bg-green-200 text-green-800'
                    )}>
                      {STATUS_LABELS[apt.status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancelaciones de peluquería */}
          {hasPermission('grooming') && (groomingCancellations?.length || 0) > 0 && (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-5 border border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                  <Ban className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-taupe-800">Clientes con Cancelaciones</h3>
                  <p className="text-sm text-red-600">Peluquería</p>
                </div>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {groomingCancellations?.slice(0, 5).map((item) => (
                  <div
                    key={item.owner_id}
                    className="flex items-center justify-between p-3 rounded-xl bg-white border border-red-100"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-taupe-400" />
                      <span className="text-sm font-medium text-taupe-800">{item.owner_name}</span>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-700">
                      {item.cancellation_count} {item.cancellation_count === 1 ? 'cancelación' : 'cancelaciones'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stock bajo */}
          {hasPermission('products_write') && (lowStockProducts?.length || 0) > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                  <TrendingDown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-taupe-800">Stock Bajo</h3>
                  <p className="text-sm text-amber-600">{lowStockProducts?.length} productos</p>
                </div>
              </div>
              <Link
                to="/dashboard/inventory"
                className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
              >
                Ver inventario
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* Acciones rápidas */}
          <div className="bg-white rounded-2xl border border-cream-200 p-5">
            <h3 className="font-semibold text-taupe-800 mb-4">Acciones Rápidas</h3>
            <div className="grid grid-cols-2 gap-3">
              {hasPermission('sales') && (
                <Link
                  to="/dashboard/sales?tab=new"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Nueva Venta</span>
                </Link>
              )}

              {hasPermission('patients') && (
                <Link
                  to="/dashboard/patients"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <PawPrint className="h-5 w-5 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Pacientes</span>
                </Link>
              )}

              {hasPermission('grooming') && (
                <Link
                  to="/dashboard/grooming"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                    <Scissors className="h-5 w-5 text-pink-600" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Peluquería</span>
                </Link>
              )}

              {hasPermission('products_write') && (
                <Link
                  to="/dashboard/inventory"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                    <Package className="h-5 w-5 text-violet-600" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Inventario</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard especializado para Tesorero/Cajero
interface CashierDashboardProps {
  user: any;
  tenant: any;
  cashStatus: any;
  pendingOrders: any[];
  formatCurrency: (value: number | string | undefined) => string;
  getGreeting: () => string;
  hasPermission: (permission: ModulePermission) => boolean;
}

function CashierDashboard({ user, tenant, cashStatus, pendingOrders, formatCurrency, getGreeting, hasPermission }: CashierDashboardProps) {
  const currentRegister = cashStatus?.current_register;
  const isOpen = cashStatus?.is_open;

  return (
    <div className="space-y-6">
      {/* Header estilo tesorero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-taupe-700 via-taupe-800 to-taupe-900 rounded-2xl p-8">
        {/* Decoraciones */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cream-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-green-400/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
          <PawPrintDecor animated className="absolute w-20 h-20 text-cream-300/10 top-4 right-8 rotate-12" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className={clsx(
                'w-3 h-3 rounded-full animate-pulse',
                isOpen ? 'bg-green-400' : 'bg-red-400'
              )}></div>
              <span className="text-cream-300 text-sm font-medium">
                {isOpen ? 'Caja Abierta' : 'Caja Cerrada'}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-cream-50">
              {getGreeting()}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-taupe-300 mt-1">
              Panel de Tesorería · {tenant?.name || 'ANIMAvet'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/cash-register"
              className={clsx(
                'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl',
                isOpen
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              )}
            >
              <Banknote className="h-4 w-4" />
              {isOpen ? 'Cerrar Caja' : 'Abrir Caja'}
            </Link>
            {hasPermission('sales') && (
              <Link
                to="/dashboard/sales?tab=new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cream-100 text-taupe-800 rounded-xl font-medium hover:bg-white transition-all shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="h-4 w-4" />
                Nueva Venta
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Estado de caja */}
      {isOpen && currentRegister && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Efectivo Inicial</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.opening_cash)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Ventas del Día</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.total_sales)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Ventas Efectivo</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.total_cash_sales)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Tarjeta + Transf</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">
              {formatCurrency((parseFloat(currentRegister.total_card_sales || '0') + parseFloat(currentRegister.total_transfer_sales || '0')))}
            </p>
          </div>
        </div>
      )}

      {/* Caja cerrada - mostrar mensaje */}
      {!isOpen && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-taupe-800 mb-2">Caja Cerrada</h3>
          <p className="text-taupe-600 mb-4">Debe abrir la caja para comenzar a registrar ventas</p>
          <Link
            to="/dashboard/cash-register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-all shadow-lg"
          >
            <Banknote className="h-5 w-5" />
            Abrir Caja
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Comandas pendientes */}
        <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-cream-100 flex items-center justify-between bg-cream-50/50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center">
                <ClipboardList className="h-4 w-4 text-orange-600" />
              </div>
              <h2 className="font-semibold text-taupe-800">Comandas por Cobrar</h2>
              {(pendingOrders?.length || 0) > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {pendingOrders?.length}
                </span>
              )}
            </div>
            <Link to="/dashboard/sales?tab=orders" className="text-sm text-taupe-600 hover:text-taupe-800 font-medium">
              Ver todas →
            </Link>
          </div>

          <div className="p-4">
            {pendingOrders && pendingOrders.length > 0 ? (
              <div className="space-y-3">
                {pendingOrders.slice(0, 5).map((order) => (
                  <Link
                    key={order.id}
                    to={`/sales?tab=orders&order=${order.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                        <ClipboardList className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-taupe-800">Comanda #{order.id}</p>
                        <p className="text-sm text-taupe-500">
                          {order.patient_name || order.owner_name || 'Sin asignar'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-taupe-800">{formatCurrency(order.total)}</p>
                      <p className="text-xs text-orange-600 font-medium">Lista para cobrar</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-7 w-7 text-green-500" />
                </div>
                <p className="text-taupe-600 font-medium">Sin comandas pendientes</p>
                <p className="text-sm text-taupe-400 mt-1">Todas las comandas han sido cobradas</p>
              </div>
            )}
          </div>
        </div>

        {/* Acciones rápidas */}
        <div className="bg-white rounded-2xl border border-cream-200 p-6">
          <h3 className="font-semibold text-taupe-800 mb-5">Acciones Rápidas</h3>
          <div className="space-y-3">
            {hasPermission('sales') && (
              <Link
                to="/dashboard/sales?tab=new"
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors group border border-green-200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-taupe-800">Nueva Venta</p>
                  <p className="text-sm text-taupe-500">Registrar venta directa</p>
                </div>
                <ArrowRight className="h-5 w-5 text-green-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}

            {hasPermission('service_orders') && (
              <Link
                to="/dashboard/sales?tab=orders"
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 transition-colors group border border-orange-200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
                  <ClipboardList className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-taupe-800">Cobrar Comandas</p>
                  <p className="text-sm text-taupe-500">
                    {(pendingOrders?.length || 0) > 0
                      ? `${pendingOrders?.length} pendientes de cobro`
                      : 'Ver comandas del día'
                    }
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}

            {hasPermission('cash_register') && (
              <Link
                to="/dashboard/cash-register"
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 hover:from-violet-100 hover:to-purple-100 transition-colors group border border-violet-200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-taupe-800">Ver Caja</p>
                  <p className="text-sm text-taupe-500">Resumen y cierre de caja</p>
                </div>
                <ArrowRight className="h-5 w-5 text-violet-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
